import { useState, useEffect } from "react";
import API from "../lib/api";
import { Search, Plus, Loader2, X, Clock, ChevronDown, ChevronUp, Check } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Slider } from "../components/ui/slider";

const CATEGORY_COLORS = {
  entertainment: "#FFD23F",
  games: "#FF5C39",
  education: "#4D96FF",
  messaging: "#34D399",
  social: "#F43F5E",
  utility: "#52526B",
  other: "#F0E4D8",
};

const TIME_PRESETS = [
  { label: "15m", value: 15 },
  { label: "30m", value: 30 },
  { label: "1h", value: 60 },
  { label: "1.5h", value: 90 },
  { label: "2h", value: 120 },
  { label: "3h", value: 180 },
];

function formatTime(mins) {
  if (!mins) return "No limit";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m/day`;
  if (m === 0) return `${h}h/day`;
  return `${h}h ${m}m/day`;
}

function AppTimeLimitEditor({ app, onSave, onClose }) {
  const [limit, setLimit] = useState(app.daily_limit_minutes || 0);
  const [hasLimit, setHasLimit] = useState(!!app.daily_limit_minutes);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(app.app_id, hasLimit ? limit : null);
    setSaved(true);
    setSaving(false);
    setTimeout(() => { setSaved(false); onClose(); }, 800);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold text-lg"
            style={{ backgroundColor: CATEGORY_COLORS[app.category] || "#F0E4D8" }}
          >
            {app.name.charAt(0)}
          </div>
          <div>
            <p className="font-extrabold text-[#1F1F2E]">{app.name}</p>
            <p className="text-xs font-semibold text-[#52526B] capitalize">{app.category}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between bg-[#FFF0E0]/50 rounded-2xl p-4">
        <div>
          <p className="font-bold text-sm text-[#1F1F2E]">Daily time limit</p>
          <p className="text-xs text-[#52526B] font-semibold">
            {hasLimit ? "App will be locked when limit is reached" : "No restrictions on usage time"}
          </p>
        </div>
        <Switch
          checked={hasLimit}
          onCheckedChange={setHasLimit}
          data-testid={`time-limit-toggle-${app.app_id}`}
          className="data-[state=checked]:bg-[#FF5C39]"
        />
      </div>

      {hasLimit && (
        <>
          <div>
            <p className="text-3xl font-extrabold text-[#FF5C39] text-center mb-4" data-testid={`time-limit-value-${app.app_id}`}>
              {formatTime(limit)}
            </p>
            <Slider
              value={[limit]}
              onValueChange={([v]) => setLimit(v)}
              min={15}
              max={300}
              step={15}
              data-testid={`time-limit-slider-${app.app_id}`}
              className="[&_[role=slider]]:bg-[#FF5C39] [&_[role=slider]]:border-0 [&_[role=slider]]:w-6 [&_[role=slider]]:h-6 [&_.range]:bg-[#FF5C39]"
            />
            <div className="flex justify-between mt-2 text-xs font-bold text-[#52526B]">
              <span>15m</span>
              <span>1h</span>
              <span>2h</span>
              <span>3h</span>
              <span>5h</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {TIME_PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => setLimit(p.value)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  limit === p.value
                    ? "bg-[#FF5C39] text-white"
                    : "bg-[#FFF0E0] text-[#FF5C39] hover:bg-[#FFD23F]/20"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={save}
        disabled={saving}
        data-testid={`save-time-limit-${app.app_id}`}
        className={`w-full font-bold rounded-2xl px-6 py-3.5 active:scale-95 transition-all flex items-center justify-center gap-2 ${
          saved ? "bg-[#34D399] text-white" : "bg-[#FF5C39] text-white hover:bg-[#E04828]"
        }`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><Check className="w-4 h-4" strokeWidth={3} /> Saved!</> : "Save limit"}
      </button>
    </div>
  );
}

export default function AppManagerPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [newApp, setNewApp] = useState({ name: "", category: "other" });
  const [adding, setAdding] = useState(false);
  const [timeLimitApp, setTimeLimitApp] = useState(null);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const { data } = await API.get("/apps");
      setApps(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleApp = async (appId, currentApproved) => {
    setApps((prev) => prev.map((a) => (a.app_id === appId ? { ...a, approved: !currentApproved } : a)));
    try {
      await API.put(`/apps/${appId}/toggle`, { approved: !currentApproved });
    } catch {
      setApps((prev) => prev.map((a) => (a.app_id === appId ? { ...a, approved: currentApproved } : a)));
    }
  };

  const saveTimeLimit = async (appId, minutes) => {
    try {
      await API.put(`/apps/${appId}/time-limit`, { daily_limit_minutes: minutes });
      setApps((prev) => prev.map((a) => (a.app_id === appId ? { ...a, daily_limit_minutes: minutes } : a)));
    } catch (err) {
      console.error(err);
    }
  };

  const addApp = async () => {
    if (!newApp.name.trim()) return;
    setAdding(true);
    try {
      const { data } = await API.post("/apps", newApp);
      setApps((prev) => [...prev, data]);
      setNewApp({ name: "", category: "other" });
      setAddOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setAdding(false);
    }
  };

  const deleteApp = async (appId) => {
    setApps((prev) => prev.filter((a) => a.app_id !== appId));
    try {
      await API.delete(`/apps/${appId}`);
    } catch {
      fetchApps();
    }
  };

  const filtered = apps.filter((a) => {
    const matchSearch = a.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || (filter === "approved" && a.approved) || (filter === "blocked" && !a.approved);
    return matchSearch && matchFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="app-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">App Manager</h1>
          <p className="text-sm font-semibold text-[#52526B] mt-1">Choose which apps are allowed and set time limits</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-app-button"
              className="bg-[#FF5C39] text-white font-bold rounded-2xl px-4 py-3 hover:bg-[#E04828] active:scale-95 transition-all shadow-sm flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              Add app
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-[32px] border-[#F0E4D8]">
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold text-[#1F1F2E]">Add a new app</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">App name</label>
                <input
                  value={newApp.name}
                  onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                  data-testid="add-app-name-input"
                  className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50"
                  placeholder="e.g. Netflix Kids"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">Category</label>
                <select
                  value={newApp.category}
                  onChange={(e) => setNewApp({ ...newApp, category: e.target.value })}
                  data-testid="add-app-category-select"
                  className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all"
                >
                  <option value="entertainment">Entertainment</option>
                  <option value="games">Games</option>
                  <option value="education">Education</option>
                  <option value="messaging">Messaging</option>
                  <option value="social">Social</option>
                  <option value="utility">Utility</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <button
                onClick={addApp}
                disabled={adding || !newApp.name.trim()}
                data-testid="add-app-submit-button"
                className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-3.5 hover:bg-[#E04828] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {adding && <Loader2 className="w-4 h-4 animate-spin" />}
                Add to device
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52526B]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="app-search-input"
            className="w-full bg-white border-2 border-[#F0E4D8] rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold outline-none focus:border-[#4D96FF] transition-all placeholder:text-[#52526B]/50"
            placeholder="Search apps..."
          />
        </div>
        <div className="flex gap-2">
          {["all", "approved", "blocked"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                filter === f
                  ? "bg-[#FF5C39] text-white shadow-sm"
                  : "bg-white text-[#52526B] border border-[#F0E4D8] hover:border-[#4D96FF]"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#1F1F2E]">{apps.length}</p>
          <p className="text-xs font-bold text-[#52526B]">Total</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#34D399]">{apps.filter((a) => a.approved).length}</p>
          <p className="text-xs font-bold text-[#52526B]">Approved</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#F43F5E]">{apps.filter((a) => !a.approved).length}</p>
          <p className="text-xs font-bold text-[#52526B]">Blocked</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 text-center">
          <p className="text-2xl font-extrabold text-[#4D96FF]">{apps.filter((a) => a.daily_limit_minutes).length}</p>
          <p className="text-xs font-bold text-[#52526B]">With limits</p>
        </div>
      </div>

      {/* App List */}
      <div className="space-y-2">
        {filtered.map((app) => (
          <div
            key={app.app_id}
            data-testid={`app-row-${app.name.toLowerCase().replace(/\s/g, "-")}`}
            className={`bg-white rounded-2xl p-4 border border-[#F0E4D8]/50 transition-all ${
              !app.approved ? "opacity-50" : ""
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold text-lg flex-shrink-0 ${
                  !app.approved ? "grayscale" : ""
                }`}
                style={{ backgroundColor: CATEGORY_COLORS[app.category] || "#F0E4D8" }}
              >
                {app.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#1F1F2E] text-sm truncate">{app.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-semibold text-[#52526B] capitalize">{app.category}</span>
                  {app.approved && app.daily_limit_minutes && (
                    <span className="text-xs font-bold text-[#4D96FF] bg-[#4D96FF]/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={3} />
                      {formatTime(app.daily_limit_minutes)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {app.approved && (
                  <button
                    onClick={() => setTimeLimitApp(app)}
                    data-testid={`time-limit-btn-${app.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-[#4D96FF] hover:bg-[#4D96FF]/10 p-2 rounded-xl transition-all"
                    title="Set time limit"
                  >
                    <Clock className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                )}
                <Switch
                  checked={app.approved}
                  onCheckedChange={() => toggleApp(app.app_id, app.approved)}
                  data-testid={`app-toggle-${app.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="data-[state=checked]:bg-[#34D399]"
                />
                <button
                  onClick={() => deleteApp(app.app_id)}
                  data-testid={`app-delete-${app.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-[#52526B] hover:text-[#F43F5E] transition-colors p-1"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#52526B] font-semibold">
            No apps found
          </div>
        )}
      </div>

      {/* Time Limit Dialog */}
      <Dialog open={!!timeLimitApp} onOpenChange={(open) => !open && setTimeLimitApp(null)}>
        <DialogContent className="bg-white rounded-[32px] border-[#F0E4D8]">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#1F1F2E]">Set daily time limit</DialogTitle>
          </DialogHeader>
          {timeLimitApp && (
            <AppTimeLimitEditor
              app={timeLimitApp}
              onSave={saveTimeLimit}
              onClose={() => setTimeLimitApp(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
